import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { startOfMonth, endOfMonth, isAfter, addMonths, format, subMonths } from 'date-fns';
import { monthMap } from 'modules/common/components/mui-tabular/constant';
import { EXCEL_ALIGNMENT, EXCEL_BORDERS, EXCEL_COLORS, EXCEL_FONTS } from './constants';
//
export const getSheetName = (key) =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
    .replace(/\s+/g, ' ')
    .substring(0, 31)
    .trim();
// Function to separate "stly_" fields into a new key for each year
const separateStlyYear = (data) => {
  const result = {};

  Object.entries(data).forEach(([year, fields]) => {
    const normalFields = [];
    const stlyFields = [];

    fields.forEach((field) => {
      if (field.startsWith('stly_')) {
        // Remove prefix here
        stlyFields.push(field.replace('stly_', ''));
      } else {
        normalFields.push(field);
      }
    });

    result[year] = normalFields;

    if (stlyFields.length > 0) {
      result[`${year}_stly`] = stlyFields;
    }
  });

  return result;
};
//
export const generateExcelAndDownload = async ({
  dataRows,
  infoData,
  legendData,
  fileName,
  selectedPeriod,
  forceReplaceHeaders = {},
}) => {
  const workbook = new ExcelJS.Workbook();

  // --- Headers formatting helper ---
  const FORCE_UPPER = ['alos', 'adr', 'iata', 'alt'];
  const formatHeader = (text) => {
    if (!text) return text;

    return text
      .replaceAll('_', ' ')
      .split(' ')
      .map((word) =>
        FORCE_UPPER.includes(word.toLowerCase())
          ? word.toUpperCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      ).map((word) => forceReplaceHeaders[word.toLowerCase()] || word)
      .join(' ');
  };

  // ---------------- Data Sheets ----------------
  Object.entries(dataRows).forEach(([key, rows]) => {
    if (!rows?.length) return;

    const sheet = workbook.addWorksheet(getSheetName(key));
    let currentRow = 1;

    // --- Main Heading ---
    const headingCell = sheet.getCell(currentRow, 1);
    headingCell.value = legendData.find((entry) => entry.type === 'heading')?.values?.[key] || ' ';
    headingCell.font = { bold: true, size: 12 };
    headingCell.alignment = EXCEL_ALIGNMENT.LEFT;
    currentRow += 1;

    // --- Info Block ---
    Object.entries(infoData).forEach(([label, value]) => {
      const row = sheet.getRow(currentRow);
      row.getCell(1).value = label;
      row.getCell(1).font = EXCEL_FONTS.BOLD;
      row.getCell(1).alignment = EXCEL_ALIGNMENT.LEFT;

      row.getCell(2).value = value;
      row.getCell(2).font = EXCEL_FONTS.NORMAL;
      row.getCell(2).alignment = EXCEL_ALIGNMENT.LEFT;

      currentRow += 1;
    });
    currentRow += 1;

    // --- Detect Years & Base Columns ---
    const allKeys = Object.keys(rows[0]);
    let yearMap = {};
    allKeys.forEach((k) => {
      const match = k.match(/^(\d{4})_(.+)$/);
      if (match) {
        const [, year, metric] = match;
        if (!yearMap[year]) yearMap[year] = [];
        if (!yearMap[year].includes(metric)) yearMap[year].push(metric);
      }
    });
    const max = Math.max(...Object.keys(yearMap).map(Number));
    yearMap = separateStlyYear(yearMap);
    //
    const years = Object.keys(yearMap).sort((a, b) => {
      const [yearA, suffixA] = a.split('_');
      const [yearB, suffixB] = b.split('_');
      //
      if (yearA !== yearB) {
        return Number(yearA) - Number(yearB);
      }
      //
      if (!suffixA && suffixB) return -1;
      if (suffixA && !suffixB) return 1;

      return 0;
    });
    const baseColumns = allKeys.filter((k) => !/^\d{4}_/.test(k));
    // --- Year Header Row ---
    const yearRowValues = [
      ...baseColumns.map(() => ''),
      ...years.flatMap((year) => Array(yearMap[year].length).fill('')),
    ];
    const yearRow = sheet.getRow(currentRow);
    yearRow.values = yearRowValues;

    let colIndex = baseColumns.length + 1;
    //
    years.forEach((year) => {
      const span = yearMap[year].length;
      sheet.mergeCells(currentRow, colIndex, currentRow, colIndex + span - 1);
      const cell = sheet.getCell(currentRow, colIndex);
      cell.value =
        year < max
          ? `${year} (Full Year)`
          : `${year?.replace('_stly', '')} (As at the end of ${selectedPeriod})`;
      cell.font = EXCEL_FONTS.WHITE_BOLD;
      cell.alignment = EXCEL_ALIGNMENT.CENTER;
      cell.fill = EXCEL_COLORS.YEAR_ROW_FILL;

      for (let i = 0; i < span; i += 1) {
        const c = sheet.getCell(currentRow, colIndex + i);
        c.border = i === span - 1 ? EXCEL_BORDERS.THICK_RIGHT : EXCEL_BORDERS.THIN;
      }
      colIndex += span;
    });

    // --- Base column headers ---
    baseColumns
      .map(() => '')
      .forEach((col, idx) => {
        const cell = sheet.getCell(currentRow, idx + 1);
        cell.value = formatHeader(col);
        cell.font = EXCEL_FONTS.WHITE_BOLD;
        cell.fill = EXCEL_COLORS.YEAR_ROW_FILL;
        cell.alignment = EXCEL_ALIGNMENT.CENTER;
        cell.border = EXCEL_BORDERS.THICK_RIGHT;
      });

    currentRow += 1;

    // --- Subheader Row (year metrics) ---
    const subHeaderValues = [
      ...baseColumns.map(formatHeader),
      ...years.flatMap((year) => yearMap[year].map((metric) => formatHeader(metric))),
    ];
    const subHeaderRow = sheet.getRow(currentRow);
    subHeaderRow.values = subHeaderValues;

    subHeaderRow.eachCell((cell, colNumber) => {
      cell.font = EXCEL_FONTS.WHITE_BOLD;
      cell.fill = EXCEL_COLORS.HEADER_FILL;
      cell.alignment = EXCEL_ALIGNMENT.CENTER;

      const isEndOfBaseCol = colNumber <= baseColumns.length;
      const isEndOfYearBlock =
        colNumber > baseColumns.length &&
        (colNumber - baseColumns.length) % yearMap[years[0]].length === 0;

      cell.border =
        isEndOfBaseCol || isEndOfYearBlock ? EXCEL_BORDERS.THICK_RIGHT : EXCEL_BORDERS.THIN;
    });
    currentRow += 1;
    // --- Data Rows ---
    rows.forEach((row) => {
      const rowValues = [
        ...baseColumns.map((col) => row[col] ?? ''),
        ...years.flatMap((year) => yearMap[year].map((metric) => row[`${year}_${metric}`] ?? '')),
      ];
      const excelRow = sheet.getRow(currentRow);
      excelRow.values = rowValues;

      excelRow.eachCell((cell, colNumber) => {
        const { value } = cell;

        // --- Border logic ---
        if (colNumber <= baseColumns.length) {
          cell.border = EXCEL_BORDERS.MEDIUM;
        } else {
          const yearBlockWidth = yearMap[years[0]].length;
          const isEndOfYearBlock = (colNumber - baseColumns.length) % yearBlockWidth === 0;
          cell.border = isEndOfYearBlock ? EXCEL_BORDERS.THICK_RIGHT : EXCEL_BORDERS.THIN;
        }

        // --- Numeric formatting
        if (typeof value === 'number') {
          // Check if the number has a fractional part
          const hasDecimal = value % 1 !== 0;
          cell.numFmt = hasDecimal ? '#,##0.##' : '#,##0';
          if (value < 0) {
            cell.font = { ...EXCEL_FONTS.NORMAL, color: { argb: 'FFFF0000' } };
          } else {
            cell.font = EXCEL_FONTS.NORMAL;
          }
        }
      });

      currentRow += 1;
    });

    // --- Auto-fit column widths ---
    sheet.columns.forEach((column) => {
      let maxLength = 0;
      //
      column.eachCell({ includeEmpty: true }, (cell) => {
        if (cell.value && typeof cell.value === 'string') {
          maxLength = Math.max(maxLength, cell.value.length);
        } else if (typeof cell.value === 'number') {
          maxLength = Math.max(maxLength, cell.value.toString().length);
        }
      });
      // Limit maximum width
      column.width = Math.min(Math.max(maxLength + 2, 10), 20);
    });
  });

  // --- Legend Sheet ---
  const legendSheet = workbook.addWorksheet('Legend');

  legendData.forEach((entry, rowIndex) => {
    const row = legendSheet.getRow(rowIndex + 1);

    if (entry.type === 'heading') {
      row.getCell(1).value = entry.values?.legend;
      legendSheet.mergeCells(rowIndex + 1, 1, rowIndex + 1, 2);
      row.getCell(1).font = { bold: true, size: 12 };
      row.getCell(1).alignment = EXCEL_ALIGNMENT.LEFT;
    } else if (entry.type === 'item') {
      row.getCell(1).value = entry.label;
      row.getCell(2).value = entry.description;
      row.getCell(1).font = { size: 10 };
      row.getCell(2).font = { size: 10 };
      row.getCell(1).alignment = EXCEL_ALIGNMENT.LEFT;
      row.getCell(2).alignment = EXCEL_ALIGNMENT.LEFT;
    }
  });

  legendSheet.columns.forEach((column) => {
    let maxLength = 10;
    column.eachCell((cell) => {
      const len = cell.value?.toString().length || 0;
      if (len > maxLength) maxLength = len;
    });
    column.width = maxLength + 2;
  });

  // --- Save Excel ---
  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${fileName}.xlsx`
  );
};

export const isEmptyObject = (obj) => {
  if (!obj) return true;
  if (Array.isArray(obj)) {
    return obj.length === 0;
  }
  if (typeof obj === 'object') {
    return Object.values(obj).every(
      (value) => value === null || (Array.isArray(value) && value.length === 0)
    );
  }
  //
  return false;
};

/**
 * Generates a list of years with complete months between start and end dates.
 * @param {Date} startDate
 * @param {Date} endDate
 * @returns {Array<Object>} e.g., [{2024: ['January', 'February', ...]}, {2025: ['January', ...]}]
 */
export const getPeriods = (startDate, endDate) => {
  const today = new Date();
  let current = startOfMonth(startDate);
  const end = startOfMonth(endDate);
  const yearsMap = {};

  while (!isAfter(current, end)) {
    const monthEnd = endOfMonth(current);

    if (!isAfter(monthEnd, today)) {
      const year = current.getFullYear();
      const monthName = format(current, 'MMMM');

      if (!yearsMap[year]) {
        yearsMap[year] = [];
      }

      yearsMap[year].push(monthName);
    }

    current = addMonths(current, 1);
  }

  return Object.entries(yearsMap).map(([year, months]) => ({ [year]: months }));
};

export const getPastNPeriods = (count = 3) => {
  const today = new Date();
  //
  const endDate = endOfMonth(subMonths(today, 1));
  const startDate = startOfMonth(subMonths(today, count));
  return getPeriods(startDate, endDate);
};

/**
 * @param {Array<Object>} visuals - base visuals
 * @param {Array<Object>} periods - [{2024: ['January', ...]}, {2025: [...]}]
 * @returns {Array<Object>} expanded visuals
 */
export const mapVisualsToRows = (visuals, periods, selectedReport, selectedYear, selectedMonth) => {
  const expanded = [];

  visuals.forEach((visual) => {
    periods.forEach((yearObj) => {
      const year = Object.keys(yearObj)[0];
      const months = yearObj[year];
      months.forEach((month) => {
        expanded.push({
          ...visual,
          year: Number(year),
          month,
        });
      });
    });
  });

  return expanded
    .filter((row) => {
      if (selectedReport && row.name !== selectedReport) return false;
      if (selectedYear && row.year !== selectedYear) return false;
      if (selectedMonth && row.month !== selectedMonth) return false;
      return true;
    })
    .sort((a, b) => {
      if (b.year !== a.year) {
        return b.year - a.year;
      }
      return monthMap[b.month] - monthMap[a.month];
    })
    .map((e, i) => ({ ...e, id: i }));
};

const formatLabel = (key) => key.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());

export const buildHeaderMap = (data, rowLabel) => {
  if (Array.isArray(data)) {
    return {
      [rowLabel]: `Account Performance - ${formatLabel(rowLabel)}`,
      legend: 'Account Performance - Legend',
    };
  }

  if (data && typeof data === 'object') {
    const headerMap = Object.keys(data).reduce((result, key) => {
      if (key !== 'legend') {
        result[key] = `Account Performance - ${formatLabel(key)}`;
      }
      return result;
    }, {});

    headerMap.legend = 'Account Performance - Legend';

    return headerMap;
  }

  return {};
};
